/** @jsxImportSource @emotion/react */
import {
  EmptyContent,
  Header,
  TruncatedString,
} from '@quickbit/backoffice-core-frontend';
import {
  Card,
  createStyles,
  Icon,
  Input,
  List,
  ListItem,
  Loader,
  Modal,
  Pagination,
  SearchInput,
  Select,
  Snackbar,
  Typography,
  UtilityButton,
  useTheme,
  Checkbox,
  Table,
} from '@quickbit/qb-design-system';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDebounce } from 'react-use';
import {
  useAddCaseNote,
  useGetCaseDetails,
  CaseCustomerType,
  CaseState,
  useUpdateCase,
  useUpdateScreeningMatchStatus,
  ScreeningMatch,
  ScreeningMatchStatus,
} from 'api/useCasesApi';
import { flattenJson } from 'utils/flattenJson';
import DowJonesEvidence from '../../../../neo-office/DowJonesEvidence';
import { dowJonesMatchLabel } from '../../../../neo-office/dowJonesEvidenceData';
import { DeleteCaseModal } from './DeleteCaseModal';
import { MatchCommentEditor } from './MatchCommentEditor';
import { PostponeCaseModal } from './PostponeCaseModal';

const SCREENING_MATCHES_PAGE_SIZE = 100;

const screeningMatchStatusOptions = [
  { value: 'NOT_INVESTIGATED', label: 'Not Investigated' },
  { value: 'FALSE_POSITIVE', label: 'False Positive' },
  { value: 'PEP_MATCH', label: 'PEP Match' },
  { value: 'RCA_MATCH', label: 'RCA Match' },
  { value: 'SIP_MATCH', label: 'Adverse Media (SIP) Match' },
  { value: 'OOL_MATCH', label: 'Other Official Lists (OOL) Match' },
  { value: 'SANCTION_MATCH', label: 'Sanction Match' },
];

export const CaseDetails = () => {
  const styles = useStyles();
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();
  const { caseId } = useParams();
  const [screeningMatchesPage, setScreeningMatchesPage] = useState(1);
  const [includePausedMedia, setIncludePausedMedia] = useState(false);
  const [screeningMatchesSearch, setScreeningMatchesSearch] = useState('');
  const [debouncedScreeningMatchesSearch, setDebouncedScreeningMatchesSearch] =
    useState('');
  const [screeningMatchesStatus, setScreeningMatchesStatus] = useState<
    ScreeningMatchStatus | undefined
  >();
  const { data, isLoading, isError, refetch } = useGetCaseDetails(
    caseId ?? '',
    screeningMatchesPage,
    SCREENING_MATCHES_PAGE_SIZE,
    debouncedScreeningMatchesSearch,
    screeningMatchesStatus,
    includePausedMedia
  );
  const addNoteMutation = useAddCaseNote(caseId ?? '');
  const updateCaseMutation = useUpdateCase(caseId ?? '');
  const [newNoteContent, setNewNoteContent] = useState('');
  const [showPostponeModal, setShowPostponeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportedToFipo, setReportedToFipo] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<ScreeningMatch | null>(
    null
  );
  const updateMatchMutation = useUpdateScreeningMatchStatus(caseId ?? '');

  useEffect(() => {
    setScreeningMatchesPage(1);
    setScreeningMatchesSearch('');
    setDebouncedScreeningMatchesSearch('');
    setScreeningMatchesStatus(undefined);
    setIncludePausedMedia(false);
  }, [caseId]);

  useEffect(() => {
    setScreeningMatchesPage(1);
  }, [
    debouncedScreeningMatchesSearch,
    screeningMatchesStatus,
    includePausedMedia,
  ]);

  useDebounce(
    () => {
      setDebouncedScreeningMatchesSearch(screeningMatchesSearch.trim());
    },
    300,
    [screeningMatchesSearch]
  );

  useEffect(() => {
    if (data?.case.reportedToFipo !== undefined) {
      setReportedToFipo(data.case.reportedToFipo);
    }
  }, [data?.case.reportedToFipo]);

  const handleReportedToFipoChange = (checked: boolean) => {
    setReportedToFipo(checked);
    updateCaseMutation.mutate({
      state: data?.case.state ?? CaseState.ACTIVE,
      reportedToFipo: checked,
    });
  };

  const handleAddNote = () => {
    if (!newNoteContent.trim()) return;
    addNoteMutation.mutate(
      { content: newNoteContent },
      {
        onSuccess: () => {
          setNewNoteContent('');
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div css={styles.loadingContainer}>
        <Loader />
      </div>
    );
  }

  if (isError || !data?.case) {
    return (
      <EmptyContent
        heading="Oops! Something went wrong."
        body="Error fetching case details"
        action={
          <UtilityButton
            text="Try again"
            variant="primary"
            onClick={() => void refetch()}
          />
        }
      />
    );
  }

  const caseItem = data.case;
  const screeningMatchesTotal =
    caseItem.screeningMatchesTotal ?? caseItem.screeningMatches.length;
  const isAwakened = caseItem.wakeupAt
    ? new Date(caseItem.wakeupAt) <= new Date()
    : false;

  const renderButtons = (customerType: CaseCustomerType) => {
    switch (customerType) {
      case 'AFFILIATE':
        return (
          <UtilityButton
            text="Show affiliate user"
            onClick={() => {
              window.open(`/users/affiliate/${caseItem.customerId}`, '_blank');
            }}
          />
        );
      case 'MERCHANT':
        return (
          <div css={styles.buttonGroup}>
            <UtilityButton
              text="Show merchant"
              onClick={() => {
                window.open(`/merchants/${caseItem.customerId}`, '_blank');
              }}
            />
            {caseItem.transactionId && (
              <UtilityButton
                text="Show transaction"
                onClick={() => {
                  window.open(
                    `/transactions/${caseItem.transactionId}`,
                    '_blank'
                  );
                }}
              />
            )}
          </div>
        );
      case 'APP':
        return (
          <div css={styles.buttonGroup}>
            <UtilityButton
              text="Show app user"
              onClick={() => {
                window.open(`/users/app/${caseItem.customerId}`, '_blank');
              }}
            />
            {caseItem.transactionId && (
              <UtilityButton
                text="Show transaction"
                onClick={() => {
                  window.open(
                    `/transactions/${caseItem.transactionId}`,
                    '_blank'
                  );
                }}
              />
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Header>
        <div css={styles.headerContent}>
          <div css={styles.back} onClick={() => navigate('/fcu/cases')}>
            <Typography variant="subtitle" color={palette.grey.x300}>
              Cases
            </Typography>
          </div>
          <div css={styles.chevron}>
            <Icon name="chevron" size={spacing.xxs} />
          </div>
          <Typography variant="subtitle">Case Details</Typography>
        </div>
      </Header>
      <div css={styles.container}>
        <Card css={styles.cardSection}>
          <Typography variant="subtitle">Data</Typography>
          <div css={styles.infoGrid}>
            <div css={styles.field}>
              <Typography variant="bodySmallest" color={palette.grey.x500}>
                Customer Type
              </Typography>
              <Typography variant="body">{caseItem.customerType}</Typography>
            </div>
            <div css={styles.field}>
              <Typography variant="bodySmallest" color={palette.grey.x500}>
                Title
              </Typography>
              <Typography variant="body">{caseItem.title}</Typography>
            </div>
            <div css={styles.field}>
              <Typography variant="bodySmallest" color={palette.grey.x500}>
                State
              </Typography>
              <Typography variant="body">
                {isAwakened
                  ? `AWAKENED (${new Date(
                      caseItem.wakeupAt as string
                    ).toLocaleDateString()})`
                  : `${caseItem.state} ${
                      caseItem.wakeupAt
                        ? `(Postponed until ${new Date(
                            caseItem.wakeupAt
                          ).toLocaleDateString()})`
                        : ''
                    }`}
              </Typography>
            </div>
            <div css={styles.field}>
              <Typography variant="bodySmallest" color={palette.grey.x500}>
                Created At
              </Typography>
              <Typography variant="body">
                {new Date(caseItem.createdAt).toLocaleString()}
              </Typography>
            </div>
          </div>
          <div css={styles.buttonSection}>
            {renderButtons(caseItem.customerType)}
          </div>
        </Card>
        <Card css={styles.cardSection}>
          <Typography variant="subtitle">Additional Information</Typography>
          {Object.keys(caseItem.additionalInformation).length > 0 ? (
            <div css={styles.infoGrid}>
              {flattenJson(caseItem.additionalInformation).map(
                ({ key, value }) => (
                  <div key={key} css={styles.field}>
                    <Typography
                      variant="bodySmallest"
                      color={palette.grey.x500}
                    >
                      {key}
                    </Typography>
                    <Typography variant="body">{value}</Typography>
                  </div>
                )
              )}
            </div>
          ) : (
            <Typography variant="body" color={palette.grey.x500}>
              No additional information
            </Typography>
          )}
        </Card>
        <Card css={styles.cardSection}>
          <Typography variant="subtitle">
            Screening Matches ({screeningMatchesTotal})
          </Typography>
          <div css={styles.matchFilters}>
            <SearchInput
              placeholder="Dow Jones profile ID"
              value={screeningMatchesSearch}
              onChange={(event) =>
                setScreeningMatchesSearch(event.target.value)
              }
            />
            <Select
              label="All statuses"
              options={screeningMatchStatusOptions}
              values={screeningMatchesStatus ? [screeningMatchesStatus] : []}
              onSelect={(values) =>
                setScreeningMatchesStatus(
                  values[0] as ScreeningMatchStatus | undefined
                )
              }
            />
          </div>

          {caseItem.state === CaseState.ACTIVE && (
            <label>
              <input
                type="checkbox"
                checked={includePausedMedia}
                onChange={(event) =>
                  setIncludePausedMedia(event.target.checked)
                }
              />{' '}
              Show paused OOL/SIP history
            </label>
          )}
          {caseItem.screeningMatches.length > 0 ? (
            <List>
              {caseItem.screeningMatches.map((match: ScreeningMatch) => {
                return (
                  <ListItem
                    css={{ '> div': { overflow: 'visible' } }}
                    key={match.uuid}
                    title={`${
                      dowJonesMatchLabel(match.matchData, match.status)
                        ? `${dowJonesMatchLabel(
                            match.matchData,
                            match.status
                          )} · `
                        : ''
                    }${match.dowJonesProfileId ?? ''}`}
                    subtitle={
                      <a
                        href={`https://eu.riskcenter.dowjones.com/search/profile?id=${match.dowJonesProfileId}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Show in Dow Jones
                      </a>
                    }
                    content={
                      <MatchCommentEditor
                        comment={match.comment}
                        onSave={(comment) => {
                          updateMatchMutation.mutate({
                            matchId: match.uuid,
                            comment,
                          });
                        }}
                      />
                    }
                    action={
                      <div css={styles.matchActions}>
                        <Select
                          label="Status"
                          options={screeningMatchStatusOptions}
                          values={[match.status]}
                          onSelect={(values) => {
                            const status = values[0] as
                              | ScreeningMatchStatus
                              | undefined;
                            if (status && status !== match.status) {
                              updateMatchMutation.mutate({
                                matchId: match.uuid,
                                status,
                              });
                            }
                          }}
                        />
                        <UtilityButton
                          text="Details"
                          variant="secondary"
                          onClick={() => setSelectedMatch(match)}
                        />
                      </div>
                    }
                  />
                );
              })}
            </List>
          ) : (
            <Typography variant="body" color={palette.grey.x500}>
              No screening matches found
            </Typography>
          )}
          {screeningMatchesTotal > SCREENING_MATCHES_PAGE_SIZE && (
            <Pagination
              currentPage={screeningMatchesPage}
              onChange={setScreeningMatchesPage}
              numberOfElements={screeningMatchesTotal}
              pageSize={SCREENING_MATCHES_PAGE_SIZE}
            />
          )}
        </Card>
        <Card css={styles.cardSection}>
          <Typography variant="subtitle">Notes</Typography>
          <div css={styles.notesContainer}>
            {caseItem.notes.length > 0 ? (
              caseItem.notes.map((note, index) => (
                <div key={index} css={styles.noteItem}>
                  <Typography variant="body">{note.content}</Typography>
                  <div css={styles.noteHeader}>
                    <Typography variant="caption" color={palette.grey.x400}>
                      {note.createdBy}
                    </Typography>
                    <Typography variant="caption" color={palette.grey.x400}>
                      {new Date(note.createdAt).toLocaleString()}
                    </Typography>
                  </div>
                </div>
              ))
            ) : (
              <Typography variant="body" color={palette.grey.x500}>
                No notes yet
              </Typography>
            )}
          </div>
          <div css={styles.addNoteSection}>
            <Input
              type="textarea"
              label="Write your note here..."
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              rows={3}
            />
            <UtilityButton
              text={addNoteMutation.isPending ? 'Adding...' : 'Add note'}
              onClick={handleAddNote}
              disabled={!newNoteContent.trim() || addNoteMutation.isPending}
              loading={addNoteMutation.isPending}
            />
          </div>
        </Card>
        <Card css={styles.cardSection}>
          <Typography variant="subtitle">Actions</Typography>
          <div css={styles.buttonGroup}>
            <span>Reported to FIPO</span>
            <Checkbox
              disabled={updateCaseMutation.isPending}
              checked={reportedToFipo}
              onChange={handleReportedToFipoChange}
            />
            <UtilityButton
              text={updateCaseMutation.isPending ? 'Closing...' : 'Close case'}
              onClick={() =>
                updateCaseMutation.mutate({
                  state: CaseState.CLOSED,
                  reportedToFipo: reportedToFipo,
                })
              }
              disabled={
                caseItem.state === CaseState.CLOSED ||
                updateCaseMutation.isPending
              }
              loading={updateCaseMutation.isPending}
            />
            <UtilityButton
              text="Postpone"
              onClick={() => setShowPostponeModal(true)}
              disabled={caseItem.state === CaseState.CLOSED}
            />
            <UtilityButton
              text="Delete"
              variant="secondary"
              onClick={() => setShowDeleteModal(true)}
            />
          </div>
        </Card>
      </div>
      <Snackbar
        open={!!updateCaseMutation.error}
        message={
          updateCaseMutation.error?.errorMessage ?? 'Failed to close case'
        }
        variant="danger"
        onClose={() => updateCaseMutation.reset()}
      />
      {selectedMatch && (
        <Modal
          title={`Screening Match Details - ${new Date(
            selectedMatch.createdAt
          ).toLocaleString()}`}
          closeModal={() => setSelectedMatch(null)}
        >
          <div css={styles.matchModalContent}>
            <DowJonesEvidence
              data={selectedMatch.matchData}
              profileId={selectedMatch.dowJonesProfileId}
            />
            <Table
              headers={[
                { label: 'Key', cellKey: 'key', sort: false },
                { label: 'Value', cellKey: 'value', sort: false },
              ]}
              rows={flattenJson(selectedMatch.matchData).map(
                ({ key, value, depth }) => ({
                  value: {
                    key: (
                      <div style={{ paddingLeft: `${depth * 32}px` }}>
                        <Typography variant="bodyBold">{key}</Typography>
                      </div>
                    ),
                    value:
                      value.length > 75 ? (
                        <TruncatedString text={value} split={75} type="end" />
                      ) : (
                        value
                      ),
                  },
                })
              )}
            />
          </div>
        </Modal>
      )}
      {showPostponeModal && (
        <PostponeCaseModal
          caseId={caseId ?? ''}
          onClose={() => setShowPostponeModal(false)}
          onSuccess={() => setShowPostponeModal(false)}
        />
      )}
      {showDeleteModal && (
        <DeleteCaseModal
          caseId={caseId ?? ''}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </>
  );
};

const useStyles = createStyles(({ spacing, palette }) => ({
  headerContent: {
    display: 'flex',
    alignItems: 'center',
  },
  back: {
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
      textDecorationColor: palette.grey.x300,
    },
  },
  chevron: {
    transform: 'rotate(180deg)',
    padding: `0 ${spacing.xxs}`,
    svg: {
      fill: 'none',
      stroke: palette.grey.x300,
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  matchFilters: {
    display: 'grid',
    gridTemplateColumns: 'minmax(240px, 1fr) minmax(200px, 320px)',
    gap: spacing.xxs,
    alignItems: 'start',
    marginTop: spacing.xxs,
    marginBottom: spacing.xxs,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.s,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: spacing.xs,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
  },
  buttonSection: {
    marginTop: spacing.xxs,
  },
  buttonGroup: {
    display: 'flex',
    gap: spacing.s,
  },
  cardSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xxs,
  },
  notesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xxs,
  },
  noteItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xxxs,
    padding: spacing.xxs,
    backgroundColor: palette.grey.x50,
    borderRadius: 8,
    borderLeft: `3px solid ${palette.primary.default}`,
  },
  noteHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  matchModalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xxs,
    marginTop: spacing.xxs,
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  matchActions: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xxs,
  },
  matchMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xxxs,
  },
  addNoteSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xxs,
    marginTop: spacing.xxxs,
  },
}));
